import React, { useState } from 'react'
import { find } from 'lodash'
import styled from 'styled-components'
import { CSS } from '@dnd-kit/utilities'
import { useSortable } from '@dnd-kit/sortable'

import { Category } from 'venue/parkMap/models'
import { Headline } from 'uiComponents/typography'
import ActionButton from 'uiComponents/buttons'
import useExpandedCategory from '../../../hooks/useExpandedCategory'
import { RowActions, RowContainer } from './shared'
import { generalIcons, animalsIcons } from '@convious/tap-icons'

const CategoryListItem = styled.div`
    border: 1px solid ${(props) => props.theme.colors.border};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    border-radius: 5px;
    padding: 8px 16px 8px 8px;
`

const CategoryColor = styled.div`
    width: 8px;
    height: 24px;
    border-radius: 2px;
    margin-right: 17px;
`

interface CategoryLiProps {
    index: number
    data: Category
    onAddPoi(): void
    onEditCategory(categoryId: string): void
    onDeleteCategory(categoryId: string): void
    children: React.ReactNode
}

const CategoryLi: React.FC<CategoryLiProps> = ({
    index,
    data,
    children,
    onEditCategory,
    onAddPoi,
    onDeleteCategory,
}) => {
    const { getExpandedIndex, saveExpandedIndex } = useExpandedCategory()
    const [expanded, setExpanded] = useState<boolean>(getExpandedIndex() === index)
    const content = find(data.content, { locale: 'en' }) ?? find(data.content, { default: true })
    const categoryIcon = find([...generalIcons, ...animalsIcons], { name: data.icon })

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: data.id,
        data: { type: 'category' },
    })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
    }

    const expand = () => {
        setExpanded(!expanded)
        saveExpandedIndex(index)
    }

    return (
        <div ref={setNodeRef} style={style}>
            <div style={{ margin: '0 0 0.5rem 0' }}>
                <CategoryListItem {...attributes} {...listeners} style={{ cursor: 'grab' }}>
                    <RowContainer>
                        <CategoryColor style={{ backgroundColor: data.color }} />
                        {categoryIcon ? (
                            <categoryIcon.icon
                                style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.313rem' }}
                            />
                        ) : (
                            <></>
                        )}
                        <Headline size={6} style={{ margin: 0 }}>
                            {content?.name}
                        </Headline>
                    </RowContainer>
                    <RowActions
                        id={data.id}
                        onEdit={onEditCategory}
                        expanded={expanded}
                        setExpanded={expand}
                        onDelete={onDeleteCategory}
                    />
                </CategoryListItem>
                {expanded && (
                    <>
                        <div>{children && <div>{children}</div>}</div>
                        <RowContainer style={{ justifyContent: 'flex-end', margin: '0.5rem 0 1rem 0' }}>
                            <ActionButton
                                secondary
                                kind="action"
                                defaultChecked
                                style={{ right: 0 }}
                                onClick={onAddPoi}
                            >
                                + Add POI
                            </ActionButton>
                        </RowContainer>
                    </>
                )}
            </div>
        </div>
    )
}

export const CategoryLiOverlay = ({ data }: { data: Category }) => {
    const content = find(data.content, { locale: 'en' }) ?? find(data.content, { default: true })
    const categoryIcon = find([...generalIcons, ...animalsIcons], { name: data.icon })

    return (
        <div style={{ margin: '0 0 0.5rem 0', boxShadow: '0 8px 16px rgba(0,0,0,0.2)', borderRadius: '5px' }}>
            <CategoryListItem style={{ cursor: 'grabbing' }}>
                <RowContainer>
                    <CategoryColor style={{ backgroundColor: data.color }} />
                    {categoryIcon ? (
                        <categoryIcon.icon
                            style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.313rem' }}
                        />
                    ) : (
                        <></>
                    )}
                    <Headline size={6} style={{ margin: 0 }}>
                        {content?.name}
                    </Headline>
                </RowContainer>
            </CategoryListItem>
        </div>
    )
}

export default CategoryLi
